// WS listener da Fila. Eventos de ciclo de vida de conversa no namespace /inbox
// invalidam a tag QUEUE → a fila ao vivo atualiza sem refresh: lead novo de
// WhatsApp aparece (conversation.created), cards saem quando alguém assume/muda
// status (claimed/status.changed/assigned/released) e o card é revalidado quando
// chega mensagem nova numa conversa já na fila (message.received → snippet/tempo
// de espera/passos da IA atualizam sem F5).
//
// invalidateTags só refaz queries com subscription ativa — mas ATENÇÃO: isso NÃO
// significa "só quando a QueuePage está montada". O TabTitleManager (montado no
// AppShell) assina `queue({ filter: 'aguardando' })` em toda aba logada, em qualquer
// tela, então esta invalidação praticamente nunca é no-op. É justamente por isso que a
// janela de coalescing abaixo importa: ela fixa o teto de GET /queue por aba.
//
// Setup: anexado uma única vez no AppProviders via attachQueueWsListener().
import type { AnyAction } from '@reduxjs/toolkit';
import { listenerMiddleware } from '@/app/store/middleware';
import { throttle, type Throttled } from '@/lib/utils/throttle';
import { queueApi } from './api';

// Mesma justificativa dos outros ws-listeners: o Dispatch genérico do
// listenerMiddleware não aceita os ThunkActions tipados de createApi sem ciclo.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseDispatch = (action: any) => any;

const QUEUE_EVENTS = new Set<string>([
  'inbox/conversation.created',
  'inbox/conversation.status.changed',
  'inbox/conversation.claimed',
  'inbox/conversation.released',
  'inbox/conversation.assigned',
  // Mensagem nova numa conversa que já está na fila (cliente respondeu, IA
  // respondeu): revalida o card pra atualizar snippet/tempo de espera/passos.
  'inbox/message.received',
  // O sweeper de 15min fechou a janela de 24h: o card muda de aba (sai de
  // "IA atendendo"/"Em atendimento", entra em "Janela expirada") sem F5.
  'inbox/conversation.window.closed',
]);

// Janela de coalescing. Substituiu um debounce trailing-only de 400ms que tinha dois
// problemas: (1) atrasava TODA atualização, mesmo um evento isolado, e (2) sob rajada
// contínua — mais de um evento a cada 400ms na empresa, que é exatamente o cenário de
// horário de pico — o cancelActiveListeners reiniciava o timer indefinidamente e a fila
// NUNCA atualizava até a rajada parar. Throttle leading+trailing conserta os dois: o
// primeiro evento refetcha na hora e a rajada colapsa numa request por janela.
const INVALIDATE_MS = 2_000;

/**
 * Registra o listener de invalidação da fila com coalescing por janela.
 * O throttle vive no escopo do attach (não do módulo) para os testes poderem
 * reanexar com clearListeners() sem herdar timer do caso anterior.
 */
export function attachQueueWsListener(): void {
  let invalidateThrottled: Throttled | null = null;

  listenerMiddleware.startListening({
    predicate: (action) => QUEUE_EVENTS.has((action as AnyAction).type),
    effect: (_action, listenerApi) => {
      invalidateThrottled ??= throttle(() => {
        (listenerApi.dispatch as LooseDispatch)(
          queueApi.util.invalidateTags([{ type: 'Conversation', id: 'QUEUE' }]),
        );
      }, INVALIDATE_MS);
      invalidateThrottled();
    },
  });
}
